import { useEffect, useState, useMemo } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import { useNavigate, useLocation } from "react-router-dom";
import { Flag, ChevronLeft, ChevronRight, Grid3x3, X, CheckCircle2, Circle } from "lucide-react";

const ExamPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { year, paper, examType, includeOfficial, includeCustom, includeCommunity, includeContingency, numQuestions, topics, timedMode, communityProvider, communityPaper } = location.state || {};

  const examDuration = examType === "mat" ? 60 * 60 : 75 * 60;

  const [allQuestions, setAllQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(examDuration);
  const [showNavigator, setShowNavigator] = useState(false);
  const [flaggedQuestions, setFlaggedQuestions] = useState({});
  const [visited, setVisited] = useState(new Set());
  const [showBuffer, setShowBuffer] = useState(false);
  const [showEndExamModal, setShowEndExamModal] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const [currentPaper, setCurrentPaper] = useState(1);
  const [showReview, setShowReview] = useState(false);
  const [reviewUnlocked, setReviewUnlocked] = useState(false);
  const [reviewTab, setReviewTab] = useState("all");
  const [showPaper2Confirm, setShowPaper2Confirm] = useState(false);
  const [paper2Started, setPaper2Started] = useState(false); // eslint-disable-line no-unused-vars
  const [paper1Answers, setPaper1Answers] = useState({});

  const isCommunityFullMock = communityProvider !== undefined && communityPaper === undefined;
  const isFullMock = (year !== undefined && paper === undefined) || isCommunityFullMock;
  const isTimed = timedMode !== false;

  const paper1Questions = useMemo(() => allQuestions.filter(q => isCommunityFullMock ? q.communityPaper === 1 : q.paper === 1), [allQuestions, isCommunityFullMock]);
  const paper2Questions = useMemo(() => allQuestions.filter(q => isCommunityFullMock ? q.communityPaper === 2 : q.paper === 2), [allQuestions, isCommunityFullMock]);
  const questions = useMemo(() => {
    if (!isFullMock) return allQuestions;
    return currentPaper === 1 ? paper1Questions : paper2Questions;
  }, [isFullMock, currentPaper, allQuestions, paper1Questions, paper2Questions]);

  useEffect(() => {
    const fetchQuestions = async () => {
      const qSnap = await getDocs(collection(db, "questions"));
      let qData = [];
      qSnap.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() };
        let include = true;
        if (communityProvider !== undefined) {
          if (communityPaper !== undefined) {
            include = data.communityProvider === communityProvider && data.communityPaper === communityPaper;
          } else {
            include = data.communityProvider === communityProvider;
          }
        } else {
          if (year !== undefined) include = include && data.year === parseInt(year);
          if (paper !== undefined) {
            const paperVal = isNaN(parseInt(paper)) ? paper : parseInt(paper);
            include = include && data.paper === paperVal;
          }
          if (includeOfficial !== undefined || includeCustom !== undefined || includeCommunity !== undefined || includeContingency !== undefined) {
            const shouldIncludeOfficial = includeOfficial !== false;
            const shouldIncludeCustom = includeCustom !== false;
            if (data.questionType === "official") include = include && shouldIncludeOfficial;
            else if (data.questionType === "custom") include = include && shouldIncludeCustom;
            else if (data.questionType === "community") include = include && (includeCommunity === true);
            else if (data.questionType === "contingency") include = include && (includeContingency === true);
          }
          if (topics && topics.length > 0 && !topics.includes("all")) include = include && topics.includes(data.topic);
        }
        if (include) qData.push(data);
      });
      if (communityProvider !== undefined || year !== undefined) {
        qData.sort((a, b) => {
          const pa = typeof a.paper === "number" ? a.paper : 0;
          const pb = typeof b.paper === "number" ? b.paper : 0;
          if (pa !== pb) return pa - pb;
          return (a.questionNumber ?? 0) - (b.questionNumber ?? 0);
        });
      } else {
        qData = qData.sort(() => Math.random() - 0.5);
      }
      if (numQuestions) setAllQuestions(qData.slice(0, numQuestions));
      else setAllQuestions(qData);
    };
    fetchQuestions();
  }, [year, paper, includeOfficial, includeCustom, includeCommunity, includeContingency, numQuestions, topics, communityProvider, communityPaper]);

  useEffect(() => {
    if (!isTimed) return;
    if (showPaper2Confirm || showBuffer) return;
    const timer = setInterval(() => setTimeLeft((t) => Math.max(t - 1, 0)), 1000);
    return () => clearInterval(timer);
  }, [showPaper2Confirm, showBuffer, isTimed]);

  useEffect(() => {
    if (!showReview) setVisited((prev) => new Set(prev).add(current));
  }, [current, showReview]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleAnswer = (val) => setAnswers({ ...answers, [current]: val });
  const toggleFlag = () => setFlaggedQuestions({ ...flaggedQuestions, [current]: !flaggedQuestions[current] });

  const getQuestionStatus = (index) => {
    if (answers[index] !== undefined) return "Complete";
    if (visited.has(index)) return "Incomplete";
    return "Unseen";
  };

  const calculateScore = () => {
    let correct = 0;
    const allAnswers = isFullMock ? { ...paper1Answers, ...answers } : answers;
    const allQs = isFullMock ? [...paper1Questions, ...paper2Questions] : questions;
    allQs.forEach((q, index) => {
      const answerKey = isFullMock && index >= paper1Questions.length ? index - paper1Questions.length : index;
      const ans = isFullMock && index >= paper1Questions.length
        ? answers[answerKey]
        : (isFullMock ? paper1Answers[answerKey] : allAnswers[index]);
      if (ans !== undefined) {
        const userAnswerIndex = q.options.indexOf(ans);
        if (q.correct === userAnswerIndex) correct++;
      }
    });
    return correct;
  };

  const calculateResults = () => {
    const totalTimeTaken = examDuration - timeLeft;
    const allAnswers = isFullMock ? { ...paper1Answers, ...answers } : answers;
    const answeredQuestions = Object.keys(allAnswers).length;
    const totalQs = isFullMock ? paper1Questions.length + paper2Questions.length : questions.length;
    const avgTimePerQuestion = answeredQuestions > 0 ? totalTimeTaken / answeredQuestions : 0;
    const score = calculateScore();
    return {
      score, total: totalQs, questionsAnswered: answeredQuestions,
      avgTime: `${Math.floor(avgTimePerQuestion)}s`,
      totalTime: `${Math.floor(totalTimeTaken / 60)}m`,
    };
  };

  useEffect(() => {
    if (showResults) {
      const results = calculateResults();
      // Save session to localStorage for Performance tab
      const paperLabel = year !== undefined
        ? (paper === "section-a" ? `MAT ${year} Section A` : paper !== undefined ? `TMUA ${year} Paper ${paper}` : `TMUA ${year}`)
        : communityProvider !== undefined
          ? (communityPaper !== undefined ? `${communityProvider} Paper ${communityPaper}` : `${communityProvider} Mock`)
          : "Custom Practice";
      const allQs = isFullMock ? [...paper1Questions, ...paper2Questions] : questions;
      const topicResults = {};
      allQs.forEach((q, index) => {
        const topic = q.topic || "Unknown";
        if (!topicResults[topic]) topicResults[topic] = { correct: 0, answered: 0 };
        const ans = isFullMock && index >= paper1Questions.length
          ? answers[index - paper1Questions.length]
          : (isFullMock ? paper1Answers[index] : answers[index]);
        if (ans !== undefined) {
          topicResults[topic].answered++;
          if (q.correct === q.options.indexOf(ans)) topicResults[topic].correct++;
        }
      });
      const session = {
        id: Date.now().toString(),
        date: new Date().toISOString().split("T")[0],
        paper: paperLabel,
        score: results.score,
        total: results.total,
        answered: results.questionsAnswered,
        pct: results.total > 0 ? Math.round(results.score / results.total * 100) : 0,
        grade: results.total > 0 ? (results.score / results.total * 9).toFixed(1) : "0.0",
        avgTimeSecs: parseInt(results.avgTime) || 0,
        totalTimeMins: parseInt(results.totalTime) || 0,
        timed: isTimed,
        topicResults,
      };
      const existing = JSON.parse(localStorage.getItem("tmuaprep_sessions") || "[]");
      existing.unshift(session);
      localStorage.setItem("tmuaprep_sessions", JSON.stringify(existing.slice(0, 100)));
      navigate("/exam-results", { state: results });
    }
  }, [showResults]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const unseenCount = useMemo(() => questions.filter((_, i) => getQuestionStatus(i) === "Unseen").length, [questions, answers, visited]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const incompleteCount = useMemo(() => questions.filter((_, i) => getQuestionStatus(i) === "Incomplete").length, [questions, answers, visited]);

  const filteredReviewQuestions = useMemo(() => {
    return questions.map((q, i) => ({ ...q, _idx: i, _status: getQuestionStatus(i), _flagged: !!flaggedQuestions[i] }))
      .filter(q => {
        if (reviewTab === "unseen") return q._status === "Unseen";
        if (reviewTab === "incomplete") return q._status === "Incomplete";
        return true;
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, reviewTab, answers, visited, flaggedQuestions]);

  const handleNext = () => {
    if (current === questions.length - 1) {
      if (isFullMock && currentPaper === 1) {
        setReviewUnlocked(true);
        setShowReview(true);
      } else {
        setShowScoreModal(true);
      }
    } else {
      setCurrent((prev) => Math.min(prev + 1, questions.length - 1));
    }
  };

  const handlePrevious = () => setCurrent((prev) => Math.max(prev - 1, 0));

  const startPaper2 = () => {
    setShowPaper2Confirm(false);
    setShowBuffer(true);
    setPaper1Answers({ ...answers });
    setTimeout(() => {
      setShowBuffer(false);
      setCurrentPaper(2);
      setPaper2Started(true);
      setAnswers({});
      setFlaggedQuestions({});
      setVisited(new Set());
      setCurrent(0);
      setTimeLeft(examDuration);
      setShowReview(false);
    }, 2000);
  };

  const renderMathHtml = (text) => {
    // Handle enumerate blocks first
    let result = text.replace(/\\begin\{enumerate\}[\s\S]*?\\end\{enumerate\}/g, (match) => {
      const contentMatch = match.match(/\\begin\{enumerate\}(?:\[.*?\])?([\s\S]*?)\\end\{enumerate\}/);
      if (!contentMatch) return "";
      const content = contentMatch[1];
      const items = [];
      const itemRegex = /\\item\s+([\s\S]*?)(?=\\item|$)/g;
      let itemMatch;
      while ((itemMatch = itemRegex.exec(content)) !== null) items.push(itemMatch[1].trim());
      if (items.length > 0) {
        const roman = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII"];
        return items.map((item, idx) => `${roman[idx]}. ${item}`).join("<br/><br/>");
      }
      return "";
    });

    // Extract math blocks into placeholders so markdown processing can't touch them
    const mathBlocks = [];
    result = result
      .replace(/\$\$([\s\S]*?)\$\$/g, (_, m) => {
        mathBlocks.push(`\\[${m.replace(/\\dfrac/g, "\\frac")}\\]`);
        return `\x00MATH${mathBlocks.length - 1}\x00`;
      })
      .replace(/\$([^$]+)\$/g, (_, m) => {
        mathBlocks.push(`\\(${m.replace(/\\dfrac/g, "\\frac")}\\)`);
        return `\x00MATH${mathBlocks.length - 1}\x00`;
      });

    // Apply text formatting now that math is safely extracted
    result = result
      .replace(/\\textbf\{([^}]+)\}/g, "<strong>$1</strong>")
      .replace(/\\textit\{([^}]+)\}/g, "<em>$1</em>")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/(?<!\*|\\)_([^_]+)_(?!\*|\\)/g, "<em>$1</em>")
      .replace(/\n/g, "<br/>");

    // Restore math blocks
    // eslint-disable-next-line no-control-regex
    result = result.replace(/\x00MATH(\d+)\x00/g, (_, i) => mathBlocks[parseInt(i)]);

    return result;
  };

  const timerColor = timeLeft < 5 * 60 ? "#f87171" : timeLeft < 10 * 60 ? "#60a5fa" : "#93c5fd";
  const answeredCount = Object.keys(answers).length;
  const progressPct = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;
  const optionLetters = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];

  /* ─── BUFFER SCREEN ─── */
  if (showBuffer) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg)]" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
        <div className="w-6 h-6 border border-[var(--border-ui)] border-t-white/60 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[var(--subtle-text)] text-xs tracking-wide">Loading Paper 2</p>
      </div>
    );
  }

  /* ─── REVIEW SCREEN ─── */
  if (showReview && !showBuffer) {
    return (
      <div className="min-h-screen flex flex-col bg-[var(--bg)] text-[var(--text)]" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
        <nav className="border-b border-[var(--border-ui)] sticky top-0 bg-[var(--bg)] z-10">
          <div className="max-w-3xl mx-auto px-6 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="font-semibold text-sm tracking-tight">tmua<span className="text-[#60a5fa]">prep</span></span>
              <span className="text-[var(--faint-text)] text-xs">|</span>
              <span className="text-[var(--subtle-text)] text-xs">Paper 1 Review</span>
            </div>
            {isTimed && (
              <span className="text-sm font-mono tabular-nums" style={{ color: timerColor }}>{formatTime(timeLeft)}</span>
            )}
          </div>
        </nav>

        <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-[11px] text-[#60a5fa] font-medium uppercase tracking-widest mb-1">Paper 1 complete</p>
              <h1 className="font-display text-3xl tracking-tight">Review your answers</h1>
            </div>
            <button
              onClick={() => setShowPaper2Confirm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--surface-hover)] hover:bg-[var(--surface-elevated)] text-[var(--text)] text-sm font-medium rounded-lg border border-[var(--border-ui)] hover:border-[var(--border-ui)] transition-all"
            >
              Continue to Paper 2 <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="flex gap-1 p-1 bg-[var(--surface-raised)] border border-[var(--border-ui)] rounded-lg mb-4 w-fit">
            {[
              { key: "all", label: `All (${questions.length})` },
              { key: "incomplete", label: `Incomplete (${incompleteCount})` },
              { key: "unseen", label: `Unseen (${unseenCount})` },
            ].map(t => (
              <button key={t.key} onClick={() => setReviewTab(t.key)}
                className={`px-3.5 py-1.5 rounded-md text-xs font-medium transition-all ${reviewTab === t.key ? "bg-[#60a5fa]/[0.1] text-[#60a5fa] border border-[#60a5fa]/[0.15]" : "text-[var(--subtle-text)] hover:text-[var(--text)]"}`}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="bg-[var(--surface)] border border-[var(--border-ui)] rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 px-5 py-2.5 border-b border-[var(--border-ui)]">
              <div className="col-span-2 text-[10px] text-[var(--faint-text)] uppercase tracking-widest">#</div>
              <div className="col-span-5 text-[10px] text-[var(--faint-text)] uppercase tracking-widest">Status</div>
              <div className="col-span-3 text-[10px] text-[var(--faint-text)] uppercase tracking-widest">Flagged</div>
              <div className="col-span-2" />
            </div>
            {filteredReviewQuestions.map((q, idx) => (
              <div key={q._idx} className={`grid grid-cols-12 px-5 py-3 items-center ${idx < filteredReviewQuestions.length - 1 ? "border-b border-[var(--border-ui)]" : ""} hover:bg-[var(--soft-fill)] transition-colors`}>
                <div className="col-span-2 text-xs text-[var(--subtle-text)]">{q._idx + 1}</div>
                <div className="col-span-5 text-xs">
                  {q._status === "Complete" ? (
                    <span className="flex items-center gap-1.5 text-[#4ade80]/80"><CheckCircle2 className="w-3 h-3" />Complete</span>
                  ) : q._status === "Incomplete" ? (
                    <span className="flex items-center gap-1.5 text-[#f87171]/80"><Circle className="w-3 h-3" />Incomplete</span>
                  ) : (
                    <span className="text-[var(--subtle-text)]">Unseen</span>
                  )}
                </div>
                <div className="col-span-3 text-xs">
                  {q._flagged && <span className="text-[#60a5fa]/70 flex items-center gap-1"><Flag className="w-3 h-3" />Yes</span>}
                </div>
                <div className="col-span-2 text-right">
                  <button onClick={() => { setCurrent(q._idx); setShowReview(false); }}
                    className="text-xs text-[#60a5fa] hover:text-[var(--text)] transition-colors">
                    Go
                  </button>
                </div>
              </div>
            ))}
          </div>
        </main>

        {showPaper2Confirm && (
          <div className="fixed inset-0 bg-[var(--overlay)] flex items-center justify-center z-50 p-4" onClick={() => setShowPaper2Confirm(false)}>
            <div className="bg-[var(--surface-raised)] border border-[var(--border-ui)] rounded-xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
              <h3 className="font-semibold text-[var(--text)] mb-1">Begin Paper 2?</h3>
              <p className="text-[var(--subtle-text)] text-sm mb-5">75 minutes. You cannot return to Paper 1.</p>
              <div className="flex gap-2">
                <button onClick={startPaper2} className="flex-1 py-2 bg-[var(--surface-hover)] hover:bg-[var(--surface-elevated)] text-[var(--text)] text-sm font-medium rounded-lg border border-[var(--border-ui)] hover:border-[var(--border-ui)] transition-all">
                  Begin Paper 2
                </button>
                <button onClick={() => setShowPaper2Confirm(false)} className="flex-1 py-2 bg-transparent hover:bg-[var(--soft-fill)] text-[var(--subtle-text)] hover:text-[var(--text)] text-sm rounded-lg border border-[var(--border-ui)] transition-all">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ─── MAIN EXAM VIEW ─── */
  return (
    <MathJaxContext>
      <div className="min-h-screen flex flex-col bg-[var(--bg)] text-[var(--text)]" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>

        {/* HEADER */}
        <nav className="border-b border-[var(--border-ui)] sticky top-0 bg-[var(--bg)] z-10 px-6">
          <div className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate("/practice", { state: { selectedSubject: "tmua" } })} className="brand-btn">tmua<span className="brand-prep">prep</span></button>
              <span className="text-[#2a2a2a] text-sm">|</span>
              <span className="text-[var(--subtle-text)] text-xs">
                {year ? (examType === "mat" ? `MAT ${year}` : `TMUA ${year}`) : communityProvider ?? "Practice"}
                {isFullMock && <span className="text-[var(--faint-text)]"> · Paper {currentPaper}</span>}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[var(--faint-text)] text-xs tabular-nums">{current + 1} / {questions.length || "?"}</span>
              {isTimed ? (
                <span className="text-sm font-mono font-medium tabular-nums" style={{ color: timerColor }}>{formatTime(timeLeft)}</span>
              ) : (
                <span className="text-xs text-[var(--faint-text)]">Untimed</span>
              )}
            </div>
          </div>
          <div className="h-px bg-[var(--surface)]">
            <div className="h-full bg-[#60a5fa]/40 transition-all duration-700" style={{ width: `${progressPct}%` }} />
          </div>
        </nav>

        {/* QUESTION AREA */}
        <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-10">
          {questions.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-6 h-6 border border-[var(--border-ui)] border-t-white/40 rounded-full animate-spin mx-auto mb-4" />
                <p className="text-[var(--faint-text)] text-xs">Loading questions</p>
              </div>
            </div>
          ) : (
            <>
              {/* Q number + flag */}
              <div className="flex items-center justify-between mb-5">
                <span className="text-[11px] text-[var(--subtle-text)] uppercase tracking-widest">Question {current + 1}</span>
                <button
                  onClick={toggleFlag}
                  className={`flex items-center gap-1.5 text-xs transition-colors ${
                    flaggedQuestions[current] ? "text-[#60a5fa]" : "text-[var(--faint-text)] hover:text-[var(--muted-text)]"
                  }`}
                >
                  <Flag className="w-3.5 h-3.5" strokeWidth={flaggedQuestions[current] ? 2 : 1.5} />
                  {flaggedQuestions[current] ? "Flagged" : "Flag"}
                </button>
              </div>

              {/* Question text */}
              <div className="mb-6">
                <MathJax dynamic>
                  <div
                    className="text-[var(--text)] text-[15px] leading-[1.8]"
                    dangerouslySetInnerHTML={{ __html: renderMathHtml(questions[current].question) }}
                  />
                </MathJax>
              </div>

              {/* Diagram */}
              {questions[current].diagramUrl && (
                <div className="mb-6">
                  <img
                    src={questions[current].diagramUrl}
                    alt="Question diagram"
                    className="max-w-full rounded-lg border border-[var(--border-ui)]"
                    style={{ maxHeight: `${360 * (questions[current].diagramScale ?? 1)}px` }}
                  />
                </div>
              )}

              {/* Divider */}
              <div className="border-t border-[var(--border-ui)] mb-5" />

              {/* Options */}
              <div className="space-y-2">
                {questions[current].options.map((opt, i) => {
                  const isSelected = answers[current] === opt;
                  return (
                    <button
                      key={i}
                      onClick={() => handleAnswer(opt)}
                      className={`w-full flex items-start gap-4 px-4 py-3.5 text-left transition-all duration-100 border-l-2 rounded-r-lg ${
                        isSelected
                          ? "border-l-[#60a5fa] bg-[#60a5fa]/[0.12]"
                          : "border-l-transparent bg-[var(--surface-raised)] hover:bg-[var(--surface-elevated)] hover:border-l-white/[0.25]"
                      }`}
                    >
                      <span className={`text-xs font-medium w-5 flex-shrink-0 mt-0.5 transition-colors ${
                        isSelected ? "text-[#60a5fa]" : "text-[var(--muted-text)]"
                      }`}>
                        {optionLetters[i]}
                      </span>
                      <MathJax dynamic>
                        <div
                          className={`text-[14px] leading-relaxed transition-colors ${isSelected ? "text-[var(--text)]" : "text-[var(--text)]"}`}
                          dangerouslySetInnerHTML={{ __html: renderMathHtml(opt) }}
                        />
                      </MathJax>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </main>

        {/* FOOTER NAV */}
        <div className="border-t border-[var(--border-ui)] bg-[var(--surface)] sticky bottom-0">
          <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
            <button
              onClick={() => setShowEndExamModal(true)}
              className="text-xs text-[var(--subtle-text)] hover:text-[#f87171] transition-colors font-medium"
            >
              End Exam
            </button>

            <div className="flex items-center gap-2">
              {isFullMock && currentPaper === 1 && reviewUnlocked && (
                <button
                  onClick={() => setShowReview(true)}
                  className="text-xs text-[#60a5fa] hover:text-[var(--text)] transition-colors px-3 py-2 border border-[#60a5fa]/[0.2] hover:border-[var(--border-ui)] rounded-lg font-medium"
                >
                  Review
                </button>
              )}
              <button
                onClick={handlePrevious}
                disabled={current === 0}
                className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium text-[var(--text-soft)] hover:text-[var(--text)] disabled:opacity-20 disabled:cursor-not-allowed bg-[var(--soft-fill)] hover:bg-[var(--soft-fill)] border border-[var(--border-ui)] hover:border-[var(--border-ui)] rounded-lg transition-all duration-150 hover:-translate-x-0.5 active:scale-95"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </button>
              <button
                onClick={() => setShowNavigator(true)}
                className="p-2 bg-[var(--soft-fill)] hover:bg-[var(--soft-fill)] border border-[var(--border-ui)] hover:border-[#60a5fa]/[0.3] rounded-lg transition-all duration-150 hover:scale-105 active:scale-95 group"
              >
                <Grid3x3 className="w-3.5 h-3.5 text-[var(--text-soft)] group-hover:text-[#60a5fa] transition-colors duration-150" />
              </button>
              <button
                onClick={handleNext}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-white text-[#000000] hover:bg-white/90 rounded-lg transition-all duration-150 hover:translate-x-0.5 active:scale-95"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* NAVIGATOR MODAL */}
        {showNavigator && (
          <div className="fixed inset-0 bg-[var(--overlay)] backdrop-blur-[2px] flex items-center justify-center z-50 p-4" style={{ animation: "backdropIn 0.18s ease" }} onClick={() => setShowNavigator(false)}>
            <div className="bg-[var(--surface)] border border-[var(--border-ui)] rounded-xl w-full max-w-[340px] shadow-xl" style={{ animation: "scaleIn 0.2s cubic-bezier(0.16,1,0.3,1)" }} onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-ui)]">
                <div>
                  <p className="text-[13px] font-medium text-white/90 tracking-tight">Questions</p>
                  <p className="text-[11px] text-white/30 mt-0.5">{answeredCount} of {questions.length} answered</p>
                </div>
                <button onClick={() => setShowNavigator(false)} className="w-6 h-6 flex items-center justify-center rounded-md text-white/25 hover:text-white/60 transition-colors duration-200">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Progress bar */}
              <div className="h-[2px] bg-[var(--soft-fill)]">
                <div
                  className="h-full bg-white/25 transition-all duration-500"
                  style={{ width: `${questions.length > 0 ? (answeredCount / questions.length) * 100 : 0}%` }}
                />
              </div>

              {/* Grid */}
              <div className="p-4 overflow-y-auto" style={{ maxHeight: "280px" }}>
                <div className="grid grid-cols-8 gap-2">
                  {questions.map((_, index) => {
                    const status = getQuestionStatus(index);
                    const isCurrent = index === current;
                    const isFlagged = flaggedQuestions[index];
                    return (
                      <button
                        key={index}
                        onClick={() => { setCurrent(index); setShowNavigator(false); }}
                        style={{ aspectRatio: "1" }}
                        className={`rounded-md text-[10px] font-medium flex items-center justify-center transition-all duration-150 ${
                          isCurrent
                            ? isFlagged
                              ? "bg-[#60a5fa] text-black border border-[#60a5fa]"
                              : status === "Complete"
                              ? "bg-[#60a5fa] text-white border border-[#60a5fa]"
                              : "bg-white text-black border border-white"
                            : isFlagged
                            ? "bg-[#60a5fa]/10 text-[#60a5fa]/70 border border-[#60a5fa]/20 hover:border-[#60a5fa]/60"
                            : status === "Complete"
                            ? "bg-[#60a5fa]/10 text-[#60a5fa]/70 border border-[#60a5fa]/20 hover:border-[#60a5fa]/60"
                            : status === "Incomplete"
                            ? "bg-[var(--soft-fill)] text-white/40 border border-white/10 hover:border-white/30"
                            : "bg-transparent text-white/20 border border-[var(--border-ui)] hover:border-white/20 hover:text-white/35"
                        }`}
                      >
                        {index + 1}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 px-5 py-3 border-t border-[var(--border-ui)]">
                <span className="flex items-center gap-1.5 text-[10px] text-white/25">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#60a5fa]/60" />Answered
                </span>
                <span className="flex items-center gap-1.5 text-[10px] text-white/25">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/30" />Visited
                </span>
                <span className="flex items-center gap-1.5 text-[10px] text-white/25">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/10" />Unseen
                </span>
                <span className="flex items-center gap-1.5 text-[10px] text-white/25">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#60a5fa]/60" />Flagged
                </span>
              </div>

            </div>
          </div>
        )}

        {/* END EXAM MODAL */}
        {showEndExamModal && (
          <div className="fixed inset-0 bg-[var(--overlay)] flex items-center justify-center z-50 p-4" onClick={() => setShowEndExamModal(false)}>
            <div className="bg-[var(--surface-raised)] border border-[var(--border-ui)] rounded-xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
              <h3 className="font-semibold text-[var(--text)] mb-1">End exam?</h3>
              <p className="text-[var(--subtle-text)] text-sm mb-5">{questions.length - answeredCount} question{questions.length - answeredCount !== 1 ? "s" : ""} unanswered. Progress will not be saved.</p>
              <div className="flex gap-2">
                <button onClick={() => navigate("/practice", { state: { selectedSubject: "tmua" } })} className="flex-1 py-2 text-[#f87171] text-sm font-medium rounded-lg border border-[#f87171]/[0.2] hover:bg-[#f87171]/[0.06] transition-all">
                  End Exam
                </button>
                <button onClick={() => setShowEndExamModal(false)} className="flex-1 py-2 text-[var(--muted-text)] hover:text-[var(--text)] text-sm rounded-lg border border-[var(--border-ui)] hover:border-[var(--border-ui)] transition-all">
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SCORE MODAL */}
        {showScoreModal && (
          <div className="fixed inset-0 bg-[var(--overlay)] flex items-center justify-center z-50 p-4" onClick={() => setShowScoreModal(false)}>
            <div className="bg-[var(--surface-raised)] border border-[var(--border-ui)] rounded-xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
              <h3 className="font-semibold text-[var(--text)] mb-1">Exam complete</h3>
              <p className="text-[var(--subtle-text)] text-sm mb-5">You have reached the end of the exam.</p>
              <div className="flex gap-2">
                <button onClick={() => { setShowScoreModal(false); setShowResults(true); }} className="flex-1 py-2 bg-[var(--surface-hover)] hover:bg-[var(--surface-elevated)] text-[var(--text)] text-sm font-medium rounded-lg border border-[var(--border-ui)] hover:border-[var(--border-ui)] transition-all">
                  See Results
                </button>
                <button onClick={() => setShowScoreModal(false)} className="flex-1 py-2 text-[var(--muted-text)] hover:text-[var(--text)] text-sm rounded-lg border border-[var(--border-ui)] hover:border-[var(--border-ui)] transition-all">
                  Review First
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MathJaxContext>
  );
};

export default ExamPage;
